import type { CsvRow } from '../types';

export interface FlattenedJsonResult {
  data: CsvRow[];
  allKeys: string[];
}

const MAX_DEPTH = 10;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function flattenObject(obj: Record<string, unknown>, prefix: string = '', depth: number = 0): CsvRow {
  const result: CsvRow = {};

  if (depth > MAX_DEPTH) {
    result[prefix || 'value'] = JSON.stringify(obj);
    return result;
  }

  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value === null || value === undefined) {
      result[newKey] = '';
    } else if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const arrayKey = `${newKey}.${i}`;
        const item = value[i];
        if (isPlainObject(item)) {
          Object.assign(result, flattenObject(item, arrayKey, depth + 1));
        } else if (item === null || item === undefined) {
          result[arrayKey] = '';
        } else if (typeof item === 'boolean') {
          result[arrayKey] = String(item);
        } else if (typeof item === 'number') {
          result[arrayKey] = item;
        } else {
          result[arrayKey] = String(item);
        }
      }
    } else if (isPlainObject(value)) {
      Object.assign(result, flattenObject(value, newKey, depth + 1));
    } else if (typeof value === 'boolean') {
      result[newKey] = String(value);
    } else if (typeof value === 'number') {
      result[newKey] = value;
    } else {
      result[newKey] = String(value);
    }
  }

  return result;
}

function extractArray(parsed: unknown): Record<string, unknown>[] {
  if (Array.isArray(parsed)) {
    return parsed.filter(item => {
      if (!isPlainObject(item)) {
        console.warn('JSON Import: Non-object array element skipped:', item);
        return false;
      }
      return true;
    });
  }

  if (isPlainObject(parsed)) {
    const keys = Object.keys(parsed);
    for (const key of keys) {
      const val = (parsed as Record<string, unknown>)[key];
      if (Array.isArray(val)) {
        console.info(`JSON Import: Using array from property "${key}"`);
        return val.filter(item => {
          if (!isPlainObject(item)) {
            console.warn('JSON Import: Non-object array element skipped:', item);
            return false;
          }
          return true;
        });
      }
    }
  }

  throw new Error('JSON muss ein Array von Objekten enthalten, oder ein Objekt mit einer Array-Property.');
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsText(file);
  });
}

export async function parseJSON(file: File): Promise<FlattenedJsonResult> {
  const text = await readFileAsText(file);

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Ungültiges JSON-Format.');
  }

  const objects = extractArray(parsed);

  if (objects.length === 0) {
    throw new Error('JSON-Datei enthält keine Daten.');
  }

  const flattenedRows = objects.map(obj => flattenObject(obj));

  // Collect all unique keys
  const allKeysSet = new Set<string>();
  for (const row of flattenedRows) {
    for (const key of Object.keys(row)) {
      allKeysSet.add(key);
    }
  }
  const allKeys = Array.from(allKeysSet).sort();

  // Normalize rows: ensure every row has every key
  const normalizedRows: CsvRow[] = flattenedRows.map(row => {
    const normalized: CsvRow = {};
    for (const key of allKeys) {
      normalized[key] = key in row ? row[key] : '';
    }
    return normalized;
  });

  return { data: normalizedRows, allKeys };
}
